import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from './firebaseConfig';
import { doc, onSnapshot, collection, addDoc, query, orderBy, updateDoc, deleteField, deleteDoc } from 'firebase/firestore';
import NewProjectModal from './NewProjectModal';
import ReactivateModal from './ReactivateModal';

function ProjectDetail({ userRole }) {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);
  
  const [formData, setFormData] = useState({ version: '', changes: '', status: 'Em desenvolvimento' });
  const [editingUpdateId, setEditingUpdateId] = useState(null);
  const [editUpdateData, setEditUpdateData] = useState({ version: '', changes: '', status: '' });

  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  useEffect(() => {
    const unsubProject = onSnapshot(doc(db, "projects", id), (snap) => {
      const data = snap.data();
      setProject(data);
      if (data) setNotes(data.notes || ''); 
    });
    const q = query(collection(db, "projects", id, "updates"), orderBy("date", "desc"));
    const unsubUpdates = onSnapshot(q, (snap) => setUpdates(snap.docs.map(d => ({id: d.id, ...d.data()}))));
    return () => { unsubProject(); unsubUpdates(); };
  }, [id]);

  const handleFinalize = async () => {
    const reason = window.prompt("Qual a justificativa para finalizar o projeto?");
    if (reason) {
      const finalizationDate = new Date();
      await updateDoc(doc(db, "projects", id), { status: 'Finalizado', finalizedAt: finalizationDate, finalizationReason: reason });
      await addDoc(collection(db, "projects", id, "updates"), { type: 'audit', changes: `Projeto finalizado em ${finalizationDate.toLocaleDateString('pt-BR')}. Motivo: ${reason}`, date: finalizationDate });
    }
  };

  const performReactivation = async (reason, newDeadline) => {
    const reactivationDate = new Date();
    await updateDoc(doc(db, "projects", id), {
      status: 'Em desenvolvimento',
      deadline: newDeadline,
      finalizedAt: deleteField(),
      finalizationReason: deleteField()
    });
    await addDoc(collection(db, "projects", id, "updates"), { type: 'audit', changes: `Projeto reativado em ${reactivationDate.toLocaleDateString('pt-BR')}. Motivo: ${reason}. Novo prazo: ${newDeadline}`, date: reactivationDate });
    setIsReactivateOpen(false);
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    const currentVersionCount = project.versionCount || 0;
    const newVersionCount = currentVersionCount + 1;
    
    await addDoc(collection(db, "projects", id, "updates"), { ...formData, date: new Date() });
    await updateDoc(doc(db, "projects", id), { 
      status: formData.status,
      versionCount: newVersionCount 
    });
    
    setFormData({ version: '', changes: '', status: 'Em desenvolvimento' });
  };

  const startEditUpdate = (up) => {
    setEditingUpdateId(up.id);
    setEditUpdateData({ version: up.version, changes: up.changes, status: up.status });
  };

  const handleSaveUpdateEdit = async (updateId) => {
    await updateDoc(doc(db, "projects", id, "updates", updateId), {
      version: editUpdateData.version,
      changes: editUpdateData.changes,
      status: editUpdateData.status
    });
    setEditingUpdateId(null);
  };

  const handleDeleteUpdate = async (updateId) => {
    if (window.confirm("Tem certeza que deseja excluir permanentemente este registro de versão?")) {
      await deleteDoc(doc(db, "projects", id, "updates", updateId));
    }
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    await updateDoc(doc(db, "projects", id), { notes: notes });
    setIsSavingNotes(false);
  };

  if (!project) return <div className="p-10 text-center">Carregando dados do projeto...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-20">
      <Link to="/" className="text-blue-600 font-semibold mb-6 inline-block">&larr; Voltar para o Dashboard</Link>
      
      <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
        <div className="flex justify-between items-start">
          <div className="w-full md:w-3/4 pr-4">
            <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
            
            <div className="mt-4 bg-blue-50 border border-blue-100 p-4 rounded-lg">
              <h4 className="font-bold text-xs text-blue-800 uppercase mb-1">Descrição do Sistema</h4>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">
                {project.description || <span className="italic text-gray-400">Nenhuma descrição foi fornecida.</span>}
              </p>
            </div>

            <p className="text-gray-500 mt-4 text-sm flex flex-wrap gap-x-4 gap-y-1">
              <span><strong>Solicitante:</strong> {project.requester || 'N/A'}</span>
              <span><strong>Início:</strong> {project.startDate ? new Date(project.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}</span>
              <span><strong>Prazo:</strong> {project.deadline ? new Date(project.deadline + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}</span>
            </p>
            
            <div className="mt-4 flex flex-wrap gap-2">
              {project.checklist?.map(item => (
                <span key={item} className="bg-gray-200 text-gray-800 text-[10px] px-2 py-1 rounded font-bold uppercase">{item}</span>
              ))}
            </div>
          </div>

          {userRole === 'admin' && (
            <div className="flex flex-col gap-2">
              <button onClick={() => setIsEditOpen(true)} className="bg-yellow-500 text-white px-4 py-2 rounded font-bold hover:bg-yellow-600 shadow-sm w-full text-center">Editar Dados</button>
              {project.status !== 'Finalizado' ? (
                <button onClick={handleFinalize} className="bg-red-600 text-white px-4 py-2 rounded font-bold hover:bg-red-700 shadow-sm w-full text-center">Finalizar</button>
              ) : (
                <button onClick={() => setIsReactivateOpen(true)} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 shadow-sm w-full text-center">Reativar</button>
              )}
            </div>
          )}
        </div>
        
        {project.status === 'Finalizado' && (
          <div className="mt-6 p-4 bg-gray-100 rounded border border-gray-300">
            <p className="font-bold text-sm text-gray-700">Projeto Finalizado em: {new Date(project.finalizedAt?.toDate()).toLocaleDateString('pt-BR')}</p>
            <p className="text-sm text-gray-600 mt-1">Justificativa: {project.finalizationReason}</p>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {userRole === 'admin' && project.status !== 'Finalizado' && (
          <form onSubmit={handleAddUpdate} className="bg-white p-5 rounded-lg shadow-sm border h-fit sticky top-6">
            <h3 className="font-bold mb-4 border-b pb-2 text-blue-800">Nova Versão</h3>
            <input className="border p-2 w-full mb-3 rounded text-sm bg-gray-50" placeholder="Ex: v1.5.0" value={formData.version} onChange={e => setFormData({...formData, version: e.target.value})} required />
            <textarea className="border p-2 w-full mb-3 rounded text-sm bg-gray-50" rows="3" placeholder="Descreva as melhorias e correções desta versão..." value={formData.changes} onChange={e => setFormData({...formData, changes: e.target.value})} required />
            <select className="border p-2 w-full mb-4 rounded text-sm bg-gray-50" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
              <option>Em desenvolvimento</option>
              <option>Homologação</option>
              <option>Produção</option>
            </select>
            <button className="bg-blue-600 text-white p-2 rounded w-full font-bold shadow-md hover:bg-blue-700 transition">Registrar e Atualizar Status</button>
          </form>
        )}

        <div className={userRole === 'admin' && project.status !== 'Finalizado' ? "md:col-span-2 space-y-3" : "md:col-span-3 space-y-3"}>
          <h3 className="font-bold text-gray-800 mb-2 border-b pb-2">Histórico de Lançamentos</h3>
          {updates.length === 0 && <p className="text-gray-500 italic">Nenhum lançamento registrado ainda.</p>}
          
          {updates.map(up => (
            <div key={up.id} className={`p-4 rounded shadow-sm border-l-4 group ${up.type === 'audit' ? 'bg-yellow-50 border-yellow-400' : 'bg-white border-blue-500'}`}>
              {editingUpdateId === up.id ? (
                <div className="bg-white p-4 rounded shadow-lg border border-yellow-400">
                  <h4 className="font-bold text-sm mb-2 text-yellow-800">Editando Registro...</h4>
                  <input className="border p-2 w-full mb-2 rounded text-sm" placeholder="Versão" value={editUpdateData.version} onChange={e => setEditUpdateData({...editUpdateData, version: e.target.value})} />
                  <textarea className="border p-2 w-full mb-2 rounded text-sm" rows="3" value={editUpdateData.changes} onChange={e => setEditUpdateData({...editUpdateData, changes: e.target.value})} />
                  <select className="border p-2 w-full mb-3 rounded text-sm" value={editUpdateData.status} onChange={e => setEditUpdateData({...editUpdateData, status: e.target.value})}>
                    <option>Em desenvolvimento</option><option>Homologação</option><option>Produção</option>
                  </select>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditingUpdateId(null)} className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded">Cancelar</button>
                    <button onClick={() => handleSaveUpdateEdit(up.id)} className="px-3 py-1 text-sm bg-green-600 text-white font-bold rounded hover:bg-green-700">Salvar</button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-bold text-sm">
                      {up.type === 'audit' ? <span className="text-yellow-700">AÇÃO ADMINISTRATIVA</span> : <span className="text-blue-700">Versão {up.version}</span>}
                    </span>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap mt-1">{up.changes}</p>
                    {up.type !== 'audit' && (
                      <div className="mt-2 text-left">
                        <span className="text-[10px] uppercase font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">{up.status}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-500 flex-shrink-0 ml-4">
                    <p>{new Date(up.date.toDate()).toLocaleDateString('pt-BR')}</p>
                    {userRole === 'admin' && up.type !== 'audit' && (
                      <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 justify-end">
                        <button onClick={() => startEditUpdate(up)} className="text-blue-600 hover:underline">Editar</button>
                        <button onClick={() => handleDeleteUpdate(up.id)} className="text-red-600 hover:underline">Excluir</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border mt-8">
        <h3 className="font-bold mb-2 flex items-center gap-2 text-yellow-700">💡 Caixa de Ideias e Anotações Futuras</h3>
        <p className="text-xs text-gray-500 mb-4">Área livre para rascunhar requisitos para próximas versões.</p>
        {userRole === 'admin' ? (
          <>
            <textarea 
              className="w-full border border-yellow-300 p-4 rounded text-sm min-h-[150px] bg-yellow-50 shadow-inner focus:outline-none focus:ring-2 focus:ring-yellow-400" 
              placeholder="Digite aqui ideias para futuras melhorias..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className="flex justify-end mt-3">
              <button 
                onClick={handleSaveNotes} 
                disabled={isSavingNotes || notes === project.notes}
                className={`px-4 py-2 rounded font-bold text-white transition ${isSavingNotes ? 'bg-gray-400' : notes === project.notes ? 'bg-gray-300' : 'bg-yellow-500 hover:bg-yellow-600 shadow-md'}`}
              >
                {isSavingNotes ? 'Salvando...' : notes === project.notes ? 'Ideias Salvas' : 'Gravar Ideias'}
              </button>
            </div>
          </>
        ) : (
          <div className="w-full border border-yellow-200 p-4 rounded text-sm min-h-[100px] bg-yellow-50 whitespace-pre-wrap text-gray-700 shadow-inner">
             {project.notes || <span className="italic text-gray-400">Nenhuma anotação registrada pelo gestor.</span>}
          </div>
        )}
      </div>
      
      {isEditOpen && <NewProjectModal existingProject={{id, ...project}} onClose={() => setIsEditOpen(false)} onSave={() => setIsEditOpen(false)} />}
      {isReactivateOpen && <ReactivateModal onClose={() => setIsReactivateOpen(false)} onConfirm={performReactivation} />}
    </div>
  );
}
export default ProjectDetail;